import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Hexagon, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import PasswordStrength, { evaluatePassword } from "@/components/PasswordStrength";

const RESEND_COOLDOWN_SECONDS = 30;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);

  // countdown tick
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const resendCode = async () => {
    if (resendCooldown > 0 || resending) return;
    if (!email) {
      toast.error("Enter your email first");
      return;
    }
    setResending(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("A new code has been sent if the account exists.");
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend code");
    } finally {
      setResending(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (!evaluatePassword(newPassword).meetsPolicy) {
      toast.error("Password doesn't meet the minimum requirements");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", {
        email,
        code: code.trim(),
        new_password: newPassword,
      });
      toast.success("Password reset. Please sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-stone-50 px-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-10">
          <div className="w-9 h-9 rounded-xl bg-yellow-400 grid place-items-center">
            <Hexagon className="w-5 h-5 text-amber-900" strokeWidth={2.5} />
          </div>
          <span className="font-display text-xl font-semibold tracking-tight">
            Hive <span className="text-yellow-500">@</span> Buzzzillion
          </span>
        </Link>

        <div className="bg-white rounded-3xl border border-stone-200 p-8" data-testid="reset-password-card">
          <div className="flex items-center gap-2 text-amber-700 mb-3">
            <ShieldCheck className="w-5 h-5" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em]">Password reset</span>
          </div>
          <h1 className="font-display text-3xl font-medium">Set a new password</h1>
          <p className="text-stone-500 text-sm mt-1">
            Enter the 6-digit code we emailed you, then choose a new password.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-password-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5"
                data-testid="reset-password-email-input"
              />
            </div>
            <div>
              <Label htmlFor="code">Reset code</Label>
              <Input
                id="code"
                type="text"
                inputMode="numeric"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
                className="mt-1.5 font-mono tracking-[0.3em] text-center text-lg"
                data-testid="reset-password-code-input"
              />
            </div>
            <div>
              <Label htmlFor="new-password">New password</Label>
              <Input
                id="new-password"
                type="password"
                required
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1.5"
                data-testid="reset-password-new-input"
              />
              <PasswordStrength password={newPassword} testidPrefix="reset-password-strength" />
            </div>
            <div>
              <Label htmlFor="confirm-password">Confirm new password</Label>
              <Input
                id="confirm-password"
                type="password"
                required
                minLength={8}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1.5"
                data-testid="reset-password-confirm-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300"
              data-testid="reset-password-submit-button"
            >
              {loading ? "Resetting…" : "Reset password"}
            </Button>
          </form>

          <div className="mt-6 pt-5 border-t border-stone-200 flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={resendCode}
              disabled={resendCooldown > 0 || resending}
              className="text-amber-700 font-medium hover:underline disabled:opacity-50 disabled:no-underline disabled:cursor-not-allowed"
              data-testid="reset-password-resend-button"
            >
              {resending
                ? "Sending…"
                : resendCooldown > 0
                  ? `Resend code in ${resendCooldown}s`
                  : "Resend code"}
            </button>
            <Link to="/login" className="text-stone-500 hover:text-stone-800" data-testid="reset-password-back-to-login">
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
